import { create } from "zustand";
import type { BoomHelpMessage } from "api/boom-help/types";
import { BOOM_HELP_SESSION_KEY_PREFIX } from "./constants";

export interface BoomHelpEntry extends BoomHelpMessage {
  rating?: "thumbs_up" | "thumbs_down";
}

interface BoomHelpSession {
  conversationId: number | null;
  messages: BoomHelpEntry[];
  consecutiveThumbsDown: number;
  handedOff: boolean;
}

interface BoomHelpState extends BoomHelpSession {
  userId: number | null;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  bindUser: (userId: number | null) => void;
  startConversation: (conversationId: number) => void;
  addMessages: (messages: BoomHelpEntry[]) => void;
  updateMessage: (id: number, patch: Partial<BoomHelpEntry>) => void;
  rate: (id: number, rating: "thumbs_up" | "thumbs_down") => void;
  markHandedOff: () => void;
  newConversation: () => void;
  reset: () => void;
}

const EMPTY_SESSION: BoomHelpSession = {
  conversationId: null,
  messages: [],
  consecutiveThumbsDown: 0,
  handedOff: false
};

const sessionKey = (userId: number) =>
  `${BOOM_HELP_SESSION_KEY_PREFIX}.${userId}`;

const readSession = (userId: number): BoomHelpSession => {
  try {
    const raw = sessionStorage.getItem(sessionKey(userId));
    return raw ? { ...EMPTY_SESSION, ...JSON.parse(raw) } : EMPTY_SESSION;
  } catch {
    return EMPTY_SESSION;
  }
};

const writeSession = (userId: number | null, session: BoomHelpSession) => {
  if (userId === null) return;
  try {
    sessionStorage.setItem(sessionKey(userId), JSON.stringify(session));
  } catch {
    return;
  }
};

const sessionOf = (state: BoomHelpSession): BoomHelpSession => ({
  conversationId: state.conversationId,
  messages: state.messages,
  consecutiveThumbsDown: state.consecutiveThumbsDown,
  handedOff: state.handedOff
});

export const useBoomHelpStore = create<BoomHelpState>((set, get) => {
  const persist = (patch: Partial<BoomHelpSession>) => {
    set(patch);
    writeSession(get().userId, sessionOf(get()));
  };

  return {
    ...EMPTY_SESSION,
    userId: null,
    isOpen: false,
    open: () => set({ isOpen: true }),
    close: () => set({ isOpen: false }),
    bindUser: (userId) => {
      if (get().userId === userId) return;
      set({
        userId,
        isOpen: false,
        ...(userId === null ? EMPTY_SESSION : readSession(userId))
      });
    },
    startConversation: (conversationId) => persist({ conversationId }),
    addMessages: (messages) =>
      persist({ messages: [...get().messages, ...messages] }),
    updateMessage: (id, patch) =>
      persist({
        messages: get().messages.map((message) =>
          message.id === id ? { ...message, ...patch } : message
        )
      }),
    rate: (id, rating) =>
      persist({
        messages: get().messages.map((message) =>
          message.id === id ? { ...message, rating } : message
        ),
        consecutiveThumbsDown:
          rating === "thumbs_down" ? get().consecutiveThumbsDown + 1 : 0
      }),
    markHandedOff: () => persist({ handedOff: true }),
    newConversation: () => persist(EMPTY_SESSION),
    reset: () => {
      const { userId } = get();
      if (userId !== null) sessionStorage.removeItem(sessionKey(userId));
      set({ ...EMPTY_SESSION, isOpen: false, userId: null });
    }
  };
});
